<script setup>
    // 학교장용 결과지 마지막 페이지 — 문구/링크: 엑셀 '학교장용 결과지 마지막 페이지' 시트
    import { computed } from 'vue';
    import { PRINCIPAL_LAST_PAGE } from './data/reportData';
    import { toSchLev } from './data/reportLogic';
    import iconStudent from '@/assets/images/result/ai/icon_student.png';
    import iconTeacher from '@/assets/images/result/ai/icon_teacher.png';
    import iconSchool from '@/assets/images/result/ai/icon_school.png';
    // 자료 카드 4장(a~d)의 표지·QR 이미지. c(AI 윤리·디지털 시민성 교육자료)만 학교급별로 표지가 다르다.
    import schoolP7A1 from '@/assets/images/result/ai/2026ai/school_p7_a_1_v2.png';
    import schoolP7A2 from '@/assets/images/result/ai/2026ai/school_p7_a_2.png';
    import schoolP7B1 from '@/assets/images/result/ai/2026ai/school_p7_b_1_v2.png';
    import schoolP7B2 from '@/assets/images/result/ai/2026ai/school_p7_b_2.jpg.png';
    import schoolP7CE1 from '@/assets/images/result/ai/2026ai/school_p7_c_e1_v2.png';
    import schoolP7CM1 from '@/assets/images/result/ai/2026ai/school_p7_c_m1_v2.png';
    import schoolP7CH1 from '@/assets/images/result/ai/2026ai/school_p7_c_h1_v2.png'; // 파일명은 c_c1이지만 실제로는 고등학교용 표지
    import schoolP7C2 from '@/assets/images/result/ai/2026ai/school_p7_c_2.jpg.png';
    import schoolP7D1 from '@/assets/images/result/ai/2026ai/school_p7_d_1_v2.png';
    import qr2 from '@/assets/images/result/ai/qr2.png'; // d(교육생태계 도움자료) QR — 학교급 공통, 새 QR 자료 없어 기존 것 재사용

    const props = defineProps({
        reportData: Object
    });

    const schLev = computed(() => toSchLev(props.reportData?.classList?.[0]?.schul_type));

    const sectionMeta = [
        { cls: 'student-section', icon: iconStudent },
        { cls: 'teacher-section', icon: iconTeacher },
        { cls: 'school-section', icon: iconSchool },
    ];
    const usageSections = computed(() =>
        (PRINCIPAL_LAST_PAGE.usage || []).map((u, i) => ({ ...u, ...(sectionMeta[i] || sectionMeta[2]) }))
    );

    // 자료 카드 4장(a~d) 표지·QR. c는 학교급(e/m/h)별로 표지가 다르고 나머지는 공통.
    const resources = computed(() => {
        const cCover = schLev.value === 'e' ? schoolP7CE1 : schLev.value === 'm' ? schoolP7CM1 : schoolP7CH1;
        const pairs = [
            [schoolP7A1, schoolP7A2],
            [schoolP7B1, schoolP7B2],
            [cCover, schoolP7C2],
            [schoolP7D1, qr2],
        ];
        return (PRINCIPAL_LAST_PAGE.resources || []).map((r, i) => {
            const [image, qr] = pairs[i] || [null, null];
            return { ...r, image, qr };
        });
    });
</script>

<template>
  <div class="report-inner">   
    <div class="card-wrap">
        <div class="card heatmap">
          <h5 class="card-title">「AI‧디지털 리터러시 진단검사」 결과, 이렇게 활용해요.</h5>
        </div>
        <div class="card mt-1">
          <div
            v-for="(section, si) in usageSections"
            :key="section.target"
            class="ai-table primary"
            :class="[section.cls, si === usageSections.length - 1 ? 'mb-3' : 'mb-1']"
          >
            <div class="row">
                <div class="th col">
                    <div class="th">
                        <img :src="section.icon" :alt="section.target">
                        <span>{{ section.target }}</span>
                    </div>
                    <div class="th">                            
                        <p v-for="item in section.items" :key="item.area">{{ item.area }}</p>
                    </div>
                </div>
                <div class="td">
                  <div class="td-content">
                      <ul>
                          <li v-for="item in section.items" :key="item.area">{{ item.method }}</li>
                      </ul>                           
                  </div>
                </div>
              </div>
          </div>

          <div class="card heatmap">
            <h5 class="card-title">AI‧디지털 리터러시 교육에 활용할 수 있는 자료는 무엇인가요?</h5>
          </div>
          <div class="resource-cards">
            <div class="resource-card" v-for="res in resources" :key="res.url">
              <h6 class="card-title">{{ res.title }}</h6>
              <hr class="divider">
              <div class="card-content">
                <template v-if="res.image">
                  <div class="book-image">
                    <img :src="res.image" :alt="res.title">
                  </div>
                  <div class="qr-code">
                    <img :src="res.qr" alt="QR코드">
                  </div>
                </template>
                <a v-if="res.url" :href="res.url" target="_blank" class="go-link">
                  바로가기 <span class="arrow">&gt;</span>
                </a>
              </div>
            </div>
          </div>

          <div class="card heatmap">
            <h5 class="card-title">「AI‧디지털 리터러시 진단검사」 결과, 이렇게 만들어집니다.</h5>
          </div>
          <!-- teacher-section 재사용: 항목 3개짜리 라벨+내용 표 레이아웃이 그대로 맞아서 쓴다(학생·가정과는 무관) -->
          <div class="ai-table primary teacher-section mb-3">
            <div class="row">
              <div class="th col">
                <div class="th" style="display:none">
                </div>
                <div class="th">
                    <p>다각도 성취 분석</p>
                    <p>개인별 맞춤 지원</p>
                    <p>전문가 수준 결정</p>
                </div>
              </div>
              <div class="td">
                <div class="td-content">
                  <ul>
                    <li>진단검사가 시행된 후 원점수를 기반으로 학생/학급/학교별 평균 및 영역별 성취율을 산출합니다.</li>
                    <li>진단검사 오답과 문항의 난이도를 정밀하게 분석하여 학생별 최적화된 학습 자료를 매칭합니다.</li>
                    <li>전문가 집단의 분석을 통해 설정된 수준 준거에 따라 학생의 최종 성취 수준을 확정합니다.</li>                                
                  </ul>                           
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ai-table {
    .row div.th.col {
        padding: 0;
       display: flex;  
        &::before { display: none; }
        .th { 
            position: relative;
            padding: 1rem 0 1rem 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 200px;
            height: 100%;
            min-width: auto;
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                right: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
            &:nth-child(1) { 
                font-weight: 600; 
                text-align: center; 
                width: 150px;
                flex-direction: column;
                gap: 0.5rem;
                
                img {
                    width: 40px;
                    height: 40px;
                }
                
                span {
                    font-size: 1rem;
                }
            }
            &:nth-child(2) { 
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 180px;
                p { font-weight: 600; color: var(--result-ai-primary-800); font-size: 19px; }
            }
        }  
    }
    .td {
        padding: 1rem .5rem 1rem 1rem !important;
        line-height: 1.5;
        text-align: justify;
        word-break: keep-all;
        text-justify: inter-word;
         > ul {
            li { 
            position: relative;
            padding-left: 24px;
            font-size: 16px;
            &::before {
                content: '';
                position: absolute;
                left: 10px;
                top: 11px;
                width: 3px;
                height: 3px;
                background-color: var(--result-ai-blak);
                border-radius: 50%;       
                }
            }
        }
        .qr-area {
            padding: 1rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            p { text-align: center; font-weight: 600; color: var(--result-ai-primary-800); }
            a { 
                font-size: 1rem; 
                color: var(--result-ai-primary-500);
                display: inline-block;
                text-decoration: underline;
                text-underline-offset: 3px;
                margin-top: 0.5rem;
            }
            .qr-placeholder {
                position: relative;
                width: 162px;
                height: 232px;
                .qr-img {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    img {
                        margin-top: 40px;
                        width: 109px;
                        height: 111px;
                    }
                }
            }
            &::before {
                content: "";
                position: absolute;
                background-color: var(--result-ai-primary-100);
                top: 50%;
                left: 0;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 10px);
            }
        }
    }
    // 학생·가정(4개)/교사(3개) 영역 — 항목 수가 달라 33.33% 같은 고정 비율 대신 flex:1로 나눈다.
    // .td 높이는 각 섹션 문구가 실제로 필요한 높이에 맞춰 섹션별로 따로 준다(공용값 200px이면
    // 학생·가정 4줄이 내용 최소 높이에 밀려 좌측 라벨과 줄이 안 맞는다).
    &.student-section .row,
    &.teacher-section .row {
        .th {
            &:nth-child(2) {
                padding: 0 !important;
                &::before { display: none; }
            }
            p {
                position: relative;
                width: 100%;
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;               
                &::before {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    top: 50%;
                    right: 0;
                    transform: translateY(-50%);
                    width: 1px;
                    height: calc(100% - 10px);
                }
                &::after {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    right : 0;
                    bottom: 0;
                    width: calc(100% - 10px);
                    height: 1px;
                }
                &:last-child::after { display: none; }
            }
        }
        .td {
            display: flex; padding: 0 !important;
            // 교사(3개) 문구 기준. 문구는 칸 안에서 세로 가운데 정렬이라 위아래 여백은 (줄 높이 - 문구 높이)/2 이다.
            // 2줄 문구(약 48px) 위아래로 8px 정도 여백이 생기도록 잡았다.
            height: 194px;
            .td-content {
                ul {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    text-align: left;
                    width: 100%;
                    li {
                        flex: 1;
                        display: flex;
                        align-items: flex-start;
                        justify-content: center;
                        flex-direction: column;
                        padding: .2rem 0.8rem;
                        position: relative;
                        line-height: 1.3;
                        width: 100%;
                        text-align: justify;
                        &::after {
                            content: "";
                            position: absolute;
                            background-color: var(--result-ai-primary-100);
                            left : 0;
                            bottom: 0;
                            width: calc(100% - 10px);
                            height: 1px;
                        }
                        &:last-child::after { display: none; }
                        a {
                            font-size: 1rem;
                            color: var(--result-ai-primary-500);
                            display: inline-block;
                            text-decoration: underline;
                            text-underline-offset: 3px;
                         }
                    }
                }
            }
        }
    }
    // 학생·가정은 4줄이라 교사(3줄)용 높이로는 문구가 못 들어가 좌측 라벨과 줄이 어긋난다.
    // 2줄 문구(약 48px) 위아래로 8px 정도 여백이 생기도록 줄당 64px × 4줄로 잡았다.
    &.student-section .row .td { height: 256px; }

    // 2개 영역 (학교)
    &.school-section .row {
        .th {
            &:nth-child(2) { 
                padding: 0 !important; 
                &::before { display: none; }            
            }           
            p {
                position: relative;
                width: 100%;
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;               
                &::before {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    top: 50%;
                    right: 0;
                    transform: translateY(-50%);
                    width: 1px;
                    height: calc(100% - 10px);
                }
                &::after {
                    content: "";
                    position: absolute;
                    background-color: var(--result-ai-primary-100);
                    right : 0;
                    bottom: 0;
                    width: calc(100% - 10px);
                    height: 1px;
                }
                &:last-child::after { display: none; }
            }
        }
        .td {
            display: flex; padding: 0 !important;
            height: 134px;
            .td-content {
                ul {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    text-align: left;
                    li {
                        flex: 1;
                        display: flex;
                        align-items: flex-start;
                        justify-content: center;
                        flex-direction: column;
                        padding: .2rem 0.8rem;
                        position: relative;
                        line-height: 1.3;
                        &::after {
                            content: "";
                            position: absolute;
                            background-color: var(--result-ai-primary-100);
                            left : 0;
                            bottom: 0;
                            width: calc(100% - 10px);
                            height: 1px;
                        }
                        &:last-child::after { display: none; }
                        a { 
                            font-size: 1rem; 
                            color: var(--result-ai-primary-500);
                            display: inline-block;
                            text-decoration: underline;
                            text-underline-offset: 3px;
                         }
                    }
                }
            }
        }
    }
    .row.thead div.th.col .th {
        &::before {
            width: 2px !important;
            height: 100% !important;
        }
    }    
}

.resource-cards {
    display: flex;
    gap: 1rem;
    margin: .5rem 0;

    .resource-card {
        flex: 1;
        background: var(--smoe-white);
        border: 1px solid var(--result-ai-primary-100);
        border-radius: 8px;
        padding: 0.5rem 1rem;
        text-align: center;

        .card-title {
            font-size: 1rem;
            font-weight: 600;
            color: var(--result-ai-primary-800);
            margin-bottom: 0.35rem;
            line-height: 1.3;
        }

        .divider {
            border: none;
            border-top: 1px solid var(--result-ai-primary-200);

        }

        .card-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.35rem;

            .book-image {
                width: 140px;
                height: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--result-ai-primary-50);
                border-radius: 4px;

                img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: cover;
                }
            }

            .qr-code {
                width: 125px;
                height: 125px;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }

            // 학생용 결과지(AiSuggestionList.vue)의 '바로가기' 버튼과 동일한 스타일
            .go-link {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                margin-top: 0;
                padding: 3px 12px;
                border: 1px solid var(--result-ai-primary-800, #566391);
                border-radius: 6px;
                background: #fff;
                color: var(--result-ai-primary-800, #566391);
                font-size: 12px;
                font-weight: 600;
                line-height: 1.6;
                text-decoration: none;
                white-space: nowrap;
                .arrow { font-size: 11px; }
            }
        }
    }
}
</style>